import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { loadListRequest } from "@/lib/listRequest";
import { fmtTL } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ReceiptText, Banknote, CreditCard, Wallet, FileText, Download, Store, Ban, Loader2, WifiOff, Undo2, Printer } from "lucide-react";
import { Pagination, ALL_SIZE } from "@/components/Pagination";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import ApprovalDialog from "@/components/ApprovalDialog";
import { useTablePrefs } from "@/lib/useTablePrefs";
import { ColumnToggle } from "@/components/ColumnToggle";
import { EmptyState } from "@/components/EmptyState";
import { ThermalDoc } from "@/components/ThermalReceipt";

const KIND_LABELS = { efatura: "e-Fatura", earchive: "e-Arşiv" };
const todayStr = () => new Date().toISOString().slice(0, 10);
const presetRange = (p) => {
  const end = new Date(); const start = new Date();
  if (p === "1w") start.setDate(end.getDate() - 7);
  else if (p === "1m") start.setMonth(end.getMonth() - 1);
  else if (p === "1y") start.setFullYear(end.getFullYear() - 1);
  else start.setDate(end.getDate() - 3);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

const SALES_COLS = [
  { key: "sale_no", label: "Fiş No", locked: true },
  { key: "created_at", label: "Tarih" },
  { key: "location", label: "Lokasyon" },
  { key: "channel", label: "Kanal" },
  { key: "customer", label: "Müşteri" },
  { key: "items", label: "Kalem" },
  { key: "payment", label: "Ödeme" },
  { key: "total", label: "Tutar", locked: true },
  { key: "edoc", label: "e-Belge", locked: true },
];

export default function SalesHistory() {
  const { can, isPrivileged, user, feature } = useAuth();
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const { visible, density, toggle: toggleCol, setDensity, reset: resetCols } = useTablePrefs("saleshistory", SALES_COLS);
  const canIssue = can("edoc.issue");
  const myLocs = user?.locations || [];
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestSeq = useRef(0);
  const [edocs, setEdocs] = useState({});
  const [dialogSale, setDialogSale] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("today"); // today | archive
  const [onlyOffline, setOnlyOffline] = useState(false);
  const [archivePreset, setArchivePreset] = useState("3d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [locId, setLocId] = useState("");
  const [locations, setLocations] = useState([]);
  const [edocPolicy, setEdocPolicy] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const approvalCredsRef = useRef([]);
  const pendingKindRef = useRef(null);
  const canCancel = can("sale.cancel") && feature("cap.return");
  const [cancelSale, setCancelSale] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNote, setCancelNote] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);
  const openCancel = (s) => { setCancelSale(s); setCancelReason(""); setCancelNote(""); };
  const submitCancel = async () => {
    if (!cancelReason) return toast.error("İptal sebebi seçin");
    if (cancelNote.trim().split(/\s+/).filter(Boolean).length < 1) return toast.error("Açıklama zorunludur (en az 1 kelime)");
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/sales/${cancelSale.id}/cancel-request`, { reason: cancelReason, note: cancelNote.trim() });
      toast.success(data.cancelled ? "Satış iptal edildi, stok geri alındı" : "İptal talebi Bölge Sorumlusu onayına gönderildi");
      setCancelSale(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İptal talebi gönderilemedi"); }
    finally { setCancelBusy(false); }
  };

  // ---- İADE (kısmi/tam) ----
  const canRefund = canCancel;
  const [refundSale, setRefundSale] = useState(null);
  const [refundQtys, setRefundQtys] = useState({});
  const [refundReason, setRefundReason] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [printDoc, setPrintDoc] = useState(null);
  const printThermal = (doc) => { setPrintDoc(doc); setTimeout(() => { window.print(); setTimeout(() => setPrintDoc(null), 800); }, 80); };
  const lineRemaining = (s, i) => {
    const sold = Number(s?.lines?.[i]?.quantity || 0);
    const done = Number((s?.refunded_lines || {})[String(i)] || 0);
    return Math.max(0, +(sold - done).toFixed(3));
  };
  const openRefund = (s) => {
    setRefundSale(s); setRefundReason(""); setRefundNote("");
    const init = {}; (s.lines || []).forEach((_, i) => { init[i] = "0"; });
    setRefundQtys(init);
  };
  const refundPreview = (s) => {
    if (!s) return { amount: 0, methods: [] };
    const gross = Number(s.subtotal || 0) + Number(s.vat_total || 0);
    const ratio = gross > 0 ? (gross - Number(s.discount || 0)) / gross : 1;
    let amount = 0;
    (s.lines || []).forEach((l, i) => {
      const q = Number(refundQtys[i] || 0);
      if (q > 0) amount += q * Number(l.unit_price || 0) * ratio;
    });
    amount = +amount.toFixed(2);
    const cash = Number(s.cash_try || 0), card = Number(s.card_try || 0), qr = Number(s.qr_try || 0);
    const base = +(cash + card + qr).toFixed(2);
    const methods = [];
    if (base <= 0) {
      if (amount > 0) methods.push({ label: s.payment_method === "card" ? "Kart" : "Nakit", amount });
    } else {
      const rCard = +(amount * card / base).toFixed(2);
      const rQr = +(amount * qr / base).toFixed(2);
      const rCash = +(amount - rCard - rQr).toFixed(2);
      if (rCard > 0) methods.push({ label: s.meal_card_provider ? `Yemek Kartı (${s.meal_card_provider})` : "Kart", amount: rCard });
      if (rQr > 0) methods.push({ label: "QR / Mobil", amount: rQr });
      if (rCash > 0) methods.push({ label: "Nakit", amount: rCash });
    }
    return { amount, methods };
  };
  const submitRefund = async () => {
    const items = Object.entries(refundQtys)
      .map(([i, q]) => ({ index: Number(i), quantity: Number(q) }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) return toast.error("İade edilecek adet girin");
    if (!refundReason) return toast.error("İade sebebi seçin");
    if (refundNote.trim().split(/\s+/).filter(Boolean).length < 1) return toast.error("Açıklama zorunludur (en az 1 kelime)");
    const src = refundSale;
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/sales/${src.id}/refund`, { items, reason: refundReason, note: refundNote.trim() });
      if (data.pending_approval) {
        toast.success(data.message || "İade tutarı onay eşiğini aştı — amir onayına gönderildi.");
        setRefundSale(null); load();
        return;
      }
      const parts = [];
      if (data.allocation?.card > 0) parts.push(`Kart ${fmtTL(data.allocation.card)}`);
      if (data.allocation?.qr > 0) parts.push(`QR ${fmtTL(data.allocation.qr)}`);
      if (data.allocation?.cash > 0) parts.push(`Nakit ${fmtTL(data.allocation.cash)}`);
      toast.success(`İade tamamlandı: ${fmtTL(data.refund_amount)} geri verildi${parts.length ? ` (${parts.join(" · ")})` : ""}. Stok güncellendi.${data.fully_refunded ? " Satış tamamen iade edildi." : ""}${data.return_edoc ? ` · ${data.return_edoc.doc_no} iade belgesi oluşturuldu.` : ""}`);
      const receipt = {
        is_refund: true, sale_no: data.refund_no, refund_of_no: src.sale_no,
        created_at: new Date().toISOString(), location_name: src.location_name, customer_name: src.customer_name,
        total: data.refund_amount, allocation: data.allocation,
        lines: items.map((it) => ({ name: src.lines[it.index]?.name, quantity: it.quantity, unit_price: src.lines[it.index]?.unit_price, vat_rate: src.lines[it.index]?.vat_rate })),
      };
      setRefundSale(null); load();
      printThermal({ type: "refund", sale: receipt, cashier: user?.name });
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İade yapılamadı"); }
    finally { setRefundBusy(false); }
  };

  const locOpts = isPrivileged ? locations : myLocs;
  const showLocFilter = isPrivileged || myLocs.length > 1;

  useEffect(() => { if (isPrivileged) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, [isPrivileged]);
  useEffect(() => { if (canCancel) api.get("/sales/cancel-reasons").then((r) => setCancelReasons(r.data.reasons || [])).catch(() => {}); }, [canCancel]);
  useEffect(() => { if (canIssue) api.get("/edoc/issue-policy").then((r) => setEdocPolicy(r.data)).catch(() => {}); }, [canIssue]);

  const loadEdocs = useCallback(() => {
    api.get("/e-belge", { params: { page_size: 100000 } })
      .then((r) => {
        const m = {};
        (r.data.items || []).forEach((d) => { if (d.source_type === "sale" && d.source_id) m[d.source_id] = d; });
        setEdocs(m);
      }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(false);
    const t = todayStr();
    const params = {};
    if (scope === "today") { params.start = `${t}T00:00:00`; }
    else {
      const r = archivePreset === "custom" ? custom : presetRange(archivePreset);
      if (r.start) params.start = `${r.start}T00:00:00`;
      if (r.end) params.end = `${r.end}T23:59:59.999999`;
    }
    if (locId) params.location_id = locId;
    loadListRequest(api.get("/sales", { params }),
      (data) => { if (seq === requestSeq.current) { setSales(data); setPage(1); } },
      () => { if (seq === requestSeq.current) setLoadError(true); }
    ).finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [scope, locId, archivePreset, custom]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEdocs(); }, [loadEdocs]);

  const doIssue = async (kind, approvals) => {
    if (!dialogSale) return false;
    setBusy(true);
    try {
      const { data } = await api.post(`/e-belge/invoice/${dialogSale.id}`, { kind: kind, approvals });
      toast.success(`${KIND_LABELS[data.kind] || "Belge"} oluşturuldu: ${data.doc_no}${data.status === "draft" ? " (taslak)" : ""}`);
      setDialogSale(null);
      loadEdocs();
      return true;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };
  const issue = async (kind) => {
    if (!dialogSale) return;
    if (edocPolicy && !edocPolicy.can_self_issue) {
      pendingKindRef.current = kind || null;
      approvalCredsRef.current = [];
      setApprovalOpen(true);
      return;
    }
    await doIssue(kind || null, null);
  };
  const onEdocApproved = async (cred) => {
    const creds = [...approvalCredsRef.current, cred];
    approvalCredsRef.current = creds;
    setApprovalOpen(false);
    const ok = await doIssue(pendingKindRef.current, creds);
    if (ok) { approvalCredsRef.current = []; }
    else { setTimeout(() => setApprovalOpen(true), 80); }  // yetersizse sıradaki yetkiliyi ekle
  };

  const downloadUbl = async (d) => {
    try {
      const res = await api.get(`/e-belge/${d.id}/ubl`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${d.doc_no}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("İndirilemedi"); }
  };

  const sortedSales = sortRows(sales, {
    sale_no: (s) => s.sale_no || "",
    created_at: (s) => s.created_at || "",
    location: (s) => s.location_name || "",
    channel: (s) => s.channel || "",
    customer: (s) => s.customer_name || "",
    items: (s) => (s.lines || []).length,
    payment: (s) => s.payment_method || "",
    total: (s) => Number(s.total) || 0,
  });
  const shownSales = onlyOffline ? sortedSales.filter((s) => s.offline) : sortedSales;
  const pageSales = pageSize >= ALL_SIZE ? shownSales : shownSales.slice((page - 1) * pageSize, page * pageSize);
  const colCount = SALES_COLS.filter((c) => visible[c.key]).length;
  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ReceiptText className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Kayıtlar</p><h1 className="font-heading text-2xl font-bold text-slate-900">Satışlar</h1></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showLocFilter && (
            <select data-testid="sales-location-filter" value={locId} onChange={(e) => setLocId(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Tüm Lokasyonlar</option>
              {locOpts.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
            {[["today", "Bugün"], ["archive", "Arşiv"]].map(([k, l]) => (
              <button key={k} data-testid={`sales-tab-${k}`} onClick={() => setScope(k)}
                className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${scope === k ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          <ColumnToggle testid="sales-table-tools" cols={SALES_COLS} visible={visible} onToggle={toggleCol} density={density} onDensity={setDensity} onReset={resetCols} />
          <button data-testid="sales-offline-filter" onClick={() => { setOnlyOffline((v) => !v); setPage(1); }}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${onlyOffline ? "bg-amber-500 text-white" : "bg-white text-amber-700 ring-1 ring-inset ring-amber-300 hover:bg-amber-50"}`}
            title="Yalnızca çevrimdışı alınıp sonradan senkronlanan satışlar">
            <WifiOff className="h-3.5 w-3.5" /> Çevrimdışı Senkron
          </button>
        </div>
      </div>

      {scope === "archive" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="sales-archive-presets">
          {[["3d", "Son 3 Gün"], ["1w", "1 Hafta"], ["1m", "1 Ay"], ["1y", "1 Yıl"], ["custom", "Özel Tarih"]].map(([k, l]) => (
            <button key={k} data-testid={`sales-preset-${k}`} onClick={() => setArchivePreset(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${archivePreset === k ? "bg-slate-800 text-white ring-slate-800" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>{l}</button>
          ))}
          {archivePreset === "custom" && (
            <div className="flex items-center gap-1">
              <DatePickerTR testid="sales-custom-from" value={custom.start} onChange={(v) => setCustom((x) => ({ ...x, start: v }))} className="mt-0 h-8 w-36" />
              <span className="text-xs text-slate-400">–</span>
              <DatePickerTR testid="sales-custom-to" value={custom.end} onChange={(v) => setCustom((x) => ({ ...x, end: v }))} className="mt-0 h-8 w-36" />
            </div>
          )}
        </div>
      )}

      <div className="max-h-[70vh] overflow-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table data-density={density} className="data-table w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-500 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
            <tr>
              {visible.sale_no && <SortTh label="Fiş No" k="sale_no" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-sale_no" />}
              {visible.created_at && <SortTh label="Tarih" k="created_at" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-created_at" />}
              {visible.location && <SortTh label="Lokasyon" k="location" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-location" />}
              {visible.channel && <SortTh label="Kanal" k="channel" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-channel" />}
              {visible.customer && <SortTh label="Müşteri" k="customer" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-customer" />}
              {visible.items && <SortTh label="Kalem" k="items" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-items" />}
              {visible.payment && <SortTh label="Ödeme" k="payment" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-payment" />}
              {visible.total && <SortTh label="Tutar" k="total" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="sales-sort-total" />}
              <th className="px-4 py-3 font-medium">e-Belge</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {!loading && loadError && (
              <tr><td colSpan={colCount} className="px-4 py-8 text-center">
                <p role="alert" className="text-sm text-red-700">Satış kayıtları yüklenemedi. Bağlantıyı kontrol edip yeniden deneyin.</p>
                <Button data-testid="sales-retry" variant="outline" size="sm" className="mt-3" onClick={load}>Yeniden Dene</Button>
              </td></tr>
            )}
            {!loading && !loadError && shownSales.length === 0 && (
              <tr><td colSpan={colCount} className="p-0">
                <EmptyState icon={onlyOffline ? WifiOff : ReceiptText} testid="sales-empty"
                  title={onlyOffline ? "Çevrimdışı senkron satış yok" : (scope === "today" ? "Bugün satış yok" : "Bu aralıkta satış yok")}
                  hint={onlyOffline ? "İnternet kesintisinde kuyruğa düşüp sonradan eşitlenen satışlar burada listelenir." : (scope === "today" ? "Kasadan ilk satış yapıldığında burada görünecek." : "Farklı bir tarih aralığı seçmeyi deneyin.")} />
              </td></tr>
            )}
            {!loading && !loadError && pageSales.map((s) => {
              const doc = edocs[s.id];
              return (
                <tr key={s.id} data-testid={`sale-row-${s.id}`} className={s.is_refund ? "bg-orange-50 hover:bg-orange-100/70" : s.cancelled ? "bg-red-50 hover:bg-red-100/70" : "hover:bg-slate-50"}>
                  {visible.sale_no && (
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className={s.is_refund ? "text-orange-600" : s.cancelled ? "text-red-400 line-through" : "text-slate-700"}>{s.sale_no}</span>
                    {s.cancelled && !s.is_refund && <span className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide text-red-600">İptal</span>}
                    {s.is_refund && s.refund_of_no && <span className="ml-1.5 align-middle text-[10px] font-semibold text-orange-500">← {s.refund_of_no}</span>}
                    {!s.cancelled && !s.is_refund && Number(s.refunded_total || 0) > 0 && <span data-testid={`sale-partial-refund-${s.id}`} className="ml-1.5 inline-flex align-middle rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">KISMİ İADE</span>}
                    {s.offline && <span data-testid={`sale-offline-${s.id}`} title="Çevrimdışı alındı, sonradan senkronlandı" className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold text-amber-700"><WifiOff className="h-2.5 w-2.5" /> ÇD</span>}
                  </td>)}
                  {visible.created_at && <td className="px-4 py-3 text-slate-500">{new Date(s.created_at).toLocaleString("tr-TR")}</td>}
                  {visible.location && <td className="px-4 py-3 text-slate-600">{s.location_name ? <span className="inline-flex items-center gap-1 text-xs"><Store className="h-3.5 w-3.5 text-slate-400" />{s.location_name}</span> : "—"}</td>}
                  {visible.channel && <td className="px-4 py-3"><span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium capitalize">{s.channel}</span></td>}
                  {visible.customer && <td className="px-4 py-3 text-slate-600">{s.customer_name || "—"}</td>}
                  {visible.items && <td className="px-4 py-3 tabular text-slate-600">{s.lines.length}</td>}
                  {visible.payment && (
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      {s.payment_method === "card" ? <CreditCard className="h-3.5 w-3.5" /> : s.payment_method === "split" ? <Wallet className="h-3.5 w-3.5" /> : <Banknote className="h-3.5 w-3.5" />}
                      {s.payment_method === "card" ? "Kart" : s.payment_method === "split" ? "Karma" : "Nakit"}
                    </span>
                  </td>)}
                  {visible.total && <td className={`px-4 py-3 text-right tabular font-bold ${s.is_refund ? "text-orange-600" : s.cancelled ? "text-red-400 line-through" : "text-brand-green"}`}>{fmtTL(s.total)}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                    {s.is_refund && <span data-testid={`sale-refund-badge-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm"><Undo2 className="h-3.5 w-3.5" /> İADE</span>}
                    {s.is_refund && (
                      <button data-testid={`refund-receipt-print-${s.id}`} onClick={() => printThermal({ type: "refund", sale: s, cashier: s.cashier })} title="İade fişini yazdır"
                        className="inline-flex items-center gap-1 rounded-md border border-orange-200 px-2 py-1 text-[11px] font-semibold text-orange-600 hover:bg-orange-50">
                        <Printer className="h-3.5 w-3.5" /> İade Fişi
                      </button>
                    )}
                    {s.cancelled && !s.is_refund && <span data-testid={`sale-cancelled-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm"><Ban className="h-3.5 w-3.5" /> İPTAL EDİLDİ</span>}
                    {!s.cancelled && !s.is_refund && (s.cancel_pending
                      ? <span data-testid={`sale-cancel-pending-${s.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">İptal onayda</span>
                      : (doc ? (
                      <button data-testid={`sale-edoc-dl-${s.id}`} onClick={() => downloadUbl(doc)} title={`${doc.doc_no} · UBL indir`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">
                        <Download className="h-3 w-3" /> {KIND_LABELS[doc.kind] || "Belge"}
                      </button>
                    ) : (s.fis_issued || s.fis_printed || !s.customer_id) ? (
                      <span data-testid={`sale-fis-${s.id}`} title="Perakende satış fişi kesildi — çift vergilendirmeyi önlemek için faturalaşamaz"
                        className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 ring-1 ring-inset ring-slate-200">
                        <ReceiptText className="h-3 w-3" /> Fiş
                      </span>
                    ) : canIssue ? (
                      <button data-testid={`sale-edoc-issue-${s.id}`} onClick={() => setDialogSale(s)}
                        className="inline-flex items-center gap-1 rounded-md border border-brand-green px-2 py-1 text-[11px] font-semibold text-brand-green hover:bg-brand-green/10">
                        <FileText className="h-3.5 w-3.5" /> Kes
                      </button>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )))}
                    {canRefund && !s.is_refund && !s.cancelled && !s.cancel_pending && (s.lines || []).some((_, i) => lineRemaining(s, i) > 0) && (
                      <button data-testid={`sale-refund-${s.id}`} onClick={() => openRefund(s)} title="İade (kısmi/tam) — ürünler stoğa döner, tutar ödeme yöntemine iade edilir ve ciro'dan düşer"
                        className="inline-flex items-center gap-1 rounded-md border border-orange-200 px-2 py-1 text-[11px] font-semibold text-orange-600 hover:bg-orange-50">
                        <Undo2 className="h-3.5 w-3.5" /> İade
                      </button>
                    )}
                    {canCancel && !s.is_refund && !s.cancelled && !s.cancel_pending && (
                      <button data-testid={`sale-cancel-${s.id}`} onClick={() => openCancel(s)} title="İşlem iptali — Bölge Sorumlusu onayına gider"
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50">
                        <Ban className="h-3.5 w-3.5" /> İptal
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && !loadError && shownSales.length > 0 && (
        <Pagination page={page} pageSize={pageSize} total={shownSales.length}
          onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} testid="sales-pagination" />
      )}

      <Dialog open={!!dialogSale} onOpenChange={(o) => !o && setDialogSale(null)}>
        <DialogContent data-testid="sale-edoc-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Belge Kes — {dialogSale?.sale_no}</DialogTitle>
            <DialogDescription>
              {dialogSale?.customer_name ? <>Müşteri: <b>{dialogSale.customer_name}</b>. </> : "Perakende (müşterisiz) satış. "}
              Kurumsal + vergi no varsa <b>e-Fatura</b>, aksi halde <b>e-Arşiv</b> önerilir.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button data-testid="edoc-issue-auto" disabled={busy} onClick={() => issue(null)} className="bg-brand-green text-white hover:bg-brand-green-600">Otomatik Seç</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="edoc-issue-efatura" disabled={busy} onClick={() => issue("efatura")} variant="outline">e-Fatura</Button>
              <Button data-testid="edoc-issue-earchive" disabled={busy} onClick={() => issue("earchive")} variant="outline">e-Arşiv</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ApprovalDialog open={approvalOpen} onClose={() => setApprovalOpen(false)} permission="edoc.issue"
        title="İrsaliye/Fatura Onayı"
        description={edocPolicy?.peer_pool_total > 0
          ? `En az ${edocPolicy.peer_need} adet 5★ yetkili onayı ya da bir Yönetici (PIN/kart/şifre). ${approvalCredsRef.current.length} onay alındı.`
          : "Yönetici onayı gerekli (PIN/kart/şifre)."}
        onApproved={onEdocApproved} />

      {/* İşlem İptali (Mağaza Müdürü → Bölge onayı) */}
      <Dialog open={!!cancelSale} onOpenChange={(o) => !o && setCancelSale(null)}>
        <DialogContent data-testid="sale-cancel-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Ban className="h-5 w-5 text-red-600" /> İşlem İptali — {cancelSale?.sale_no}</DialogTitle>
            <DialogDescription>
              {user?.role === "admin" ? "İşlem iptal edilecek ve stok geri alınacak." : "İptal talebi Bölge Sorumlusu onayına gönderilir. Sebep ve açıklama zorunludur."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="overline">İptal Sebebi *</label>
              <select data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                <option value="">Sebep seçin...</option>
                {cancelReasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Açıklama *</label>
              <textarea data-testid="cancel-note" value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} rows={3}
                placeholder="Açıklama Giriniz..."
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <Button data-testid="cancel-submit" onClick={submitCancel} disabled={cancelBusy} className="w-full bg-red-600 text-white hover:bg-red-700">
              {cancelBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />} {user?.role === "admin" ? "İptal Et" : "İptal Talebini Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* İade (Kısmi/Tam) — ürün stoğa döner, tutar ödeme yöntemine iade edilir, ciro'dan düşer */}
      <Dialog open={!!refundSale} onOpenChange={(o) => !o && setRefundSale(null)}>
        <DialogContent data-testid="sale-refund-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Undo2 className="h-5 w-5 text-orange-600" /> İade — {refundSale?.sale_no}</DialogTitle>
            <DialogDescription>
              İade edilecek ürün ve adetleri seçin. Ürünler stoğa geri eklenir, tutar orijinal ödeme yöntemine
              (kart→kart, nakit→nakit, QR→QR) iade edilir ve satış ciro'sundan düşülür.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="max-h-56 divide-y overflow-y-auto rounded-md border border-slate-200">
              {(refundSale?.lines || []).map((l, i) => {
                const rem = refundSale ? lineRemaining(refundSale, i) : 0;
                return (
                  <div key={i} data-testid={`refund-line-${i}`} className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-700">{l.name}</div>
                      <div className="text-[11px] text-slate-400">Satılan {Number(l.quantity)} × {fmtTL(l.unit_price)} · İade edilebilir: <b className={rem > 0 ? "text-orange-600" : "text-slate-400"}>{rem}</b></div>
                    </div>
                    <input type="number" min="0" max={rem} step="any" disabled={rem <= 0}
                      data-testid={`refund-qty-${i}`}
                      value={refundQtys[i] ?? "0"}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") { setRefundQtys((m) => ({ ...m, [i]: "" })); return; }
                        const n = Math.max(0, Math.min(Number(v) || 0, rem));
                        setRefundQtys((m) => ({ ...m, [i]: String(n) }));
                      }}
                      className="h-9 w-20 rounded-md border border-input bg-white px-2 text-right text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-slate-50 disabled:text-slate-300" />
                  </div>
                );
              })}
            </div>
            {(() => {
              const p = refundPreview(refundSale);
              return (
                <div data-testid="refund-preview" className="rounded-md bg-orange-50 px-3 py-2 ring-1 ring-inset ring-orange-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-600">İade Tutarı</span>
                    <span data-testid="refund-amount" className="text-lg font-bold text-orange-600">{fmtTL(p.amount)}</span>
                  </div>
                  {p.methods.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {p.methods.map((m, k) => (
                        <span key={k} data-testid={`refund-method-${k}`} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">{m.label}: {fmtTL(m.amount)}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <div>
              <label className="overline">İade Sebebi *</label>
              <select data-testid="refund-reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                <option value="">Sebep seçin...</option>
                {cancelReasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Açıklama *</label>
              <textarea data-testid="refund-note" value={refundNote} onChange={(e) => setRefundNote(e.target.value)} rows={2}
                placeholder="Açıklama Giriniz..."
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <Button data-testid="refund-submit" onClick={submitRefund} disabled={refundBusy || refundPreview(refundSale).amount <= 0}
              className="w-full bg-orange-600 text-white hover:bg-orange-700">
              {refundBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />} İadeyi Onayla
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ThermalDoc doc={printDoc} />
    </div>
  );
}
